import * as React from 'react';

const FLOATING_HEADER_ANIMATION_TIMEOUT = 200;

interface IUseFloatingHeaderParams {
  floatingHeaderScrollRef: React.RefObject<HTMLDivElement | null>;
  headerRowRef: React.RefObject<HTMLDivElement | null>;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}

interface IUseFloatingHeaderResult {
  isFloatingHeaderMounted: boolean;
  isFloatingHeaderVisible: boolean;
  syncFloatingHeaderScroll(scrollLeft: number): void;
  syncTableScroll(scrollLeft: number): void;
}

export function useFloatingHeader({
  floatingHeaderScrollRef,
  headerRowRef,
  scrollRef,
}: IUseFloatingHeaderParams): IUseFloatingHeaderResult {
  const [isFloatingHeaderVisible, setFloatingHeaderVisible] = React.useState(false);
  const [isFloatingHeaderMounted, setFloatingHeaderMounted] = React.useState(false);

  React.useEffect(() => {
    const headerRow = headerRowRef.current;

    if (!headerRow) {
      return;
    }

    const updateFloatingHeaderVisibility = () => {
      setFloatingHeaderVisible(headerRow.getBoundingClientRect().bottom <= 0);
    };

    const observer = new IntersectionObserver(updateFloatingHeaderVisibility, { threshold: 0 });
    observer.observe(headerRow);
    updateFloatingHeaderVisibility();

    return () => {
      observer.disconnect();
    };
  }, [headerRowRef]);

  React.useEffect(() => {
    if (isFloatingHeaderMounted && scrollRef.current && floatingHeaderScrollRef.current) {
      floatingHeaderScrollRef.current.scrollLeft = scrollRef.current.scrollLeft;
    }
  }, [floatingHeaderScrollRef, isFloatingHeaderMounted, isFloatingHeaderVisible, scrollRef]);

  React.useEffect(() => {
    if (isFloatingHeaderVisible) {
      setFloatingHeaderMounted(true);

      return;
    }

    const timeout = window.setTimeout(() => {
      setFloatingHeaderMounted(false);
    }, FLOATING_HEADER_ANIMATION_TIMEOUT);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [isFloatingHeaderVisible]);

  const syncFloatingHeaderScroll = (scrollLeft: number) => {
    if (floatingHeaderScrollRef.current) {
      floatingHeaderScrollRef.current.scrollLeft = scrollLeft;
    }
  };

  const syncTableScroll = (scrollLeft: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollLeft;
    }
  };

  return {
    isFloatingHeaderMounted,
    isFloatingHeaderVisible,
    syncFloatingHeaderScroll,
    syncTableScroll,
  };
}
